import React from 'react';
import { Button } from "@/components/ui/button";
import {
  Calendar, Clock, Home, Sparkles, ArrowRight, Star, Heart, Users, CheckCircle,
  Monitor, Laptop, Video, Wifi, Globe, Phone, Shield, Target, Zap, FileText,
  MapPin, type LucideIcon,
} from "lucide-react";
import { openGoogleCalendar } from "@/lib/utils";

const ICON_MAP: Record<string, LucideIcon> = {
  Clock, Home, Sparkles, Calendar, Star, Heart, Users, CheckCircle,
  Monitor, Laptop, Video, Wifi, Globe, Phone, Shield, Target, Zap, FileText, MapPin,
};

interface FeatureItem {
  icon: string;
  title: string;
  desc: string;
}

interface OnlineConsultationProps {
  headerIcon?: string;
  title?: string;
  desc?: string;
  buttonText?: string;
  buttonUrl?: string;
  features?: FeatureItem[];
}

const DEFAULT_FEATURES: FeatureItem[] = [
  {
    icon: "Clock",
    title: "Horários Flexíveis",
    desc: "Agendamento conforme sua disponibilidade, inclusive noturno e fins de semana.",
  },
  {
    icon: "Home",
    title: "Conforto de Casa",
    desc: "Atendimento completo sem sair de onde você estiver, com total privacidade.",
  },
  {
    icon: "Sparkles",
    title: "Mesma Qualidade",
    desc: "Consulta personalizada com o mesmo cuidado e atenção do atendimento presencial.",
  },
];

export const OnlineConsultation: React.FC<OnlineConsultationProps> = ({
  headerIcon = "Monitor",
  title = "Consulta Online",
  desc = "Não pode se deslocar? Atendimento de qualidade onde você estiver, com a mesma dedicação da consulta presencial.",
  buttonText = "Agendar Online",
  buttonUrl = "",
  features = DEFAULT_FEATURES,
}) => {
  const HeaderIcon = ICON_MAP[headerIcon] ?? Monitor;
  const handleButtonClick = () => {
    if (buttonUrl) {
      window.open(buttonUrl, '_blank', 'noopener,noreferrer');
    } else {
      openGoogleCalendar();
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-6 max-w-5xl">
        <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col lg:flex-row">

          {/* ── Painel esquerdo — brand ─────────────────── */}
          <div className="bg-brand-primary lg:w-80 lg:shrink-0 flex flex-col justify-between p-8 gap-8">
            <div>
              <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center mb-5">
                <HeaderIcon className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-primary leading-tight mb-3">
                {title}
              </h2>
              <p className="text-white/70 text-sm leading-relaxed">
                {desc}
              </p>
            </div>

            <Button
              size="lg"
              className="w-full bg-white text-brand-primary hover:bg-white/90 font-primary font-semibold shadow-md group"
              onClick={handleButtonClick}
            >
              <Calendar className="w-4 h-4 mr-2" />
              {buttonText}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>

          {/* ── Painel direito — features ───────────────── */}
          <div className="bg-white flex-1 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            {features.map(({ icon, title: featureTitle, desc: featureDesc }) => {
              const Icon = ICON_MAP[icon] ?? Clock;
              return (
                <div key={featureTitle} className="flex flex-col items-start gap-3 p-7">
                  <div className="w-10 h-10 rounded-xl bg-brand-primary/8 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 font-primary mb-1">{featureTitle}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{featureDesc}</p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};
